/* eslint-disable react/forbid-component-props */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { Element } from 'react-scroll';
import { REVIEWS_COMPONENT } from '../../config/constants/scroll';
import { BRAND_WOWCHER, CYF_DEAL_TYPE } from '../../config/setup/setup';
import {
  AUTHENTIC,
  TIMER_PRICE_DROP_HEADING,
  TIMER_DEAL_ENDS_SOON_HEADING,
} from '../../config/text/text';
import { isCreditecDeal } from '../../helpers/creditec';
import { useSocialCues } from '../../helpers/deals';
import {
  getScrollElementDealButtonType,
  mainDealLayoutType,
} from '../../helpers/scroll';
import {
  getBackendSocialCueMessage,
  getRedTextDealSocialCue,
} from '../../helpers/socialCues';
import ThemeContext from '../../providers/ThemeProvider';
import TimerTo, { TIMER_TYPES } from '../_generic/timer/TimerTo';
import MainDealBoughtCount from './MainDealBoughtCount';
import MainDealCYFBanner from './MainDealCYFBanner';
import MainDealDiscount from './MainDealDiscount';
import MainDealReviews from './MainDealReviews';
import MainDealSocialCue from './mainDealSocialCue';
import MainDealWas from './mainDealWas';
import DealPriceSplat from './shared-components/DealPriceSplat';
import PostageAndPackagingText from './shared-components/PostageAndPackagingText';

const MainDealMetaBlock = ({
  position,
  deal = null,
  getGiftingButton = () => {},
  hasPostage = false,
  isClaim = false,
  isClosed = false,
  isGoogleHotel = false,
  isMobile = false,
  pageDetails,
  reviewsCollapsed = false,
  columnSelection,
  rowSelection,
  isProductPage = false,
  setReviewsCollapsed = () => {},
  splatVisible = true,
}) => {
  const theme = useContext(ThemeContext);
  const socialCuesData = useSocialCues(
    deal?.id,
    isClosed || deal?.totalRemaining <= 0,
  );
  const scrollTargetName = getScrollElementDealButtonType(
    deal,
    mainDealLayoutType.actions,
    pageDetails.isTravel,
  );

  return (
    <>
      <div>
        <div className="meta-block">
          <PostageAndPackagingText
            className="margin"
            deal={deal}
            isMain
            show={hasPostage}
          />
          {!isMobile && (
            <div className="meta-block__timer">
              <TimerTo
                heading={
                  deal.dealExpiryTimerEndDate
                    ? TIMER_DEAL_ENDS_SOON_HEADING
                    : TIMER_PRICE_DROP_HEADING
                }
                headingPosition="left"
                id={deal.id}
                isMarketingClaim
                redirectToTC={deal.termsAndConditions}
                type={TIMER_TYPES.deal}
                unixTimestampTo={
                  deal.dealExpiryTimerEndDate || deal.priceDropTimerEndDate
                }
              />
            </div>
          )}
          <div className="meta-block__content">
            <div
              className={classNames('meta-block__info', {
                'hide-desktop': Boolean(pageDetails.isTravel),
              })}
            >
              <MainDealBoughtCount center deal={deal} />
              {!isClaim && (
                <>
                  <MainDealDiscount
                    columnSelection={columnSelection}
                    deal={deal}
                    isProductPage={isProductPage}
                    position={position}
                    rowSelection={rowSelection}
                  />
                  <MainDealWas deal={deal} />
                </>
              )}
            </div>
            {isMobile && (
              <div className="meta-block__flex">
                {getGiftingButton('mobile', scrollTargetName, pageDetails)}
                <div>
                  <TimerTo
                    heading={
                      deal.dealExpiryTimerEndDate
                        ? TIMER_DEAL_ENDS_SOON_HEADING
                        : TIMER_PRICE_DROP_HEADING
                    }
                    id={deal.id}
                    isMarketingClaim
                    redirectToTC={deal.termsAndConditions}
                    type={TIMER_TYPES.deal}
                    unixTimestampTo={
                      deal.dealExpiryTimerEndDate || deal.priceDropTimerEndDate
                    }
                  />
                </div>
              </div>
            )}
            {isMobile && (
              <>
                <Element name={REVIEWS_COMPONENT} />
                <MainDealReviews
                  collapsed={reviewsCollapsed}
                  deal={deal}
                  isTravel={pageDetails.isTravel}
                  onToggle={() => setReviewsCollapsed((state) => !state)}
                  pagination
                />
              </>
            )}
            {isMobile && !isProductPage && (
              <DealPriceSplat
                className="main-deal--mobile hide-desktop"
                columnSelection={columnSelection}
                deal={deal}
                forcePrice
                isGoogleHotel={isGoogleHotel}
                isHideIfNoDiscount={false}
                isHideSplat={isClaim}
                isProductPage={isProductPage}
                rowSelection={rowSelection}
                visible={splatVisible}
              />
            )}
          </div>
        </div>
        <MainDealSocialCue
          className="main-deal-social-cue--assure hide-mobile"
          cue={
            deal.display?.bought &&
            deal.totalBought === 0 &&
            !isCreditecDeal(deal) &&
            deal.newDealMessageTag
              ? deal.newDealMessageTag
              : ''
          }
        />
        <MainDealSocialCue
          className="main-deal-social-cue--alert"
          cue={getRedTextDealSocialCue({
            deal,
          })}
          isStarMarked
        />
        <MainDealSocialCue
          className="main-deal-social-cue--assure"
          cue={getBackendSocialCueMessage(socialCuesData)}
        />
        <MainDealSocialCue
          className="main-deal-social-cue--authentic"
          cue={
            deal.display?.previousDeal
              ? AUTHENTIC[process.env.NEXT_PUBLIC_BRAND || BRAND_WOWCHER]
              : ''
          }
        />
        {deal.dealType === CYF_DEAL_TYPE && <MainDealCYFBanner />}
      </div>
      <style jsx>{`
        .meta-block__content {
          display: flex;
          margin-bottom: 8px;
          margin-top: 8px;
          align-items: flex-start;
        }
        .meta-block__flex {
          display: flex;
        }
        :global(.meta-block .pp-text) {
          position: absolute;
          top: 4px;
        }
        .meta-block__info {
          display: flex;
          flex-shrink: 0;
          margin-left: auto;
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .meta-block__content {
            flex-direction: column;
          }
          .meta-block__info {
            width: ${deal.display?.discountAmount
              ? 'calc(100% - 85px)'
              : '100%'};

            align-items: center;
            justify-content: ${deal.display?.discountAmount
              ? 'space-evenly'
              : 'start'};
            margin-bottom: 16px;
          }
          :global(.meta-block .pp-text) {
            position: relative;
          }
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          width: 100%;
        }
      `}</style>
    </>
  );
};

MainDealMetaBlock.propTypes = {
  columnSelection: PropTypes.number,
  deal: PropTypes.object,
  getGiftingButton: PropTypes.func,
  hasPostage: PropTypes.bool,
  isClaim: PropTypes.bool,
  isClosed: PropTypes.bool,
  isGoogleHotel: PropTypes.bool,
  isMobile: PropTypes.bool,
  isProductPage: PropTypes.bool,
  pageDetails: PropTypes.object.isRequired,
  position: PropTypes.any,
  reviewsCollapsed: PropTypes.bool,
  rowSelection: PropTypes.number,
  setReviewsCollapsed: PropTypes.func,
  splatVisible: PropTypes.bool,
};

export default MainDealMetaBlock;
